import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import { dashboardAPI, peoplePlatformAPI } from '@/services';
import { ledgerApi } from '@/services/api';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Relevé & compte : situation agrégée de la caisse organisationnelle (Ledgera + effectifs People + stats Entrea).
 * Les mouvements ligne à ligne : /my-fund/financial.
 */
const CAISSE_PATHS = new Set(['/my-fund']);

const ReleveCompteOrganisation = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { pathname } = useLocation();
  const { error } = useToast();
  const { role } = usePermissions();
  const { user } = useAuth();
  const { settings } = useSettings();
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);

  const orgName =
    String(settings?.organization?.name || t('releve.defaultOrgName')).trim() || t('releve.defaultOrgName');
  /** Index /my-fund : intitulés Mon organisation / Ma caisse. */
  const isMaCaisse = CAISSE_PATHS.has(pathname);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [dashboardRes, peopleRes, ledgerBalRes, ledgerStatsRes] = await Promise.all([
          dashboardAPI.getStats().catch(() => ({ data: {} })),
          peoplePlatformAPI.getStatsSummary().catch(() => ({ data: {} })),
          ledgerApi.getOrgBalance().catch(() => ({ data: {} })),
          ledgerApi.getStatsSummary().catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const base = dashboardRes.data || {};
        const people = peopleRes.data || {};
        const byType = (ledgerStatsRes.data?.by_tx_type || []).reduce((acc, row) => {
          const k = String(row.tx_type ?? row._id ?? '').toLowerCase();
          if (!k) return acc;
          acc[k] = Number(row.total || 0);
          return acc;
        }, {});
        const credit = num(byType.credit);
        const debit = num(byType.debit);
        setSnapshot({
          total_members: num(base.total_members ?? people.total),
          active_members: num(base.active_members ?? people.active),
          pending_members: num(base.pending_members ?? people.pending),
          total_balance: num(base.total_balance ?? ledgerBalRes.data?.current_balance),
          total_contributions: num(base.total_contributions ?? credit),
          total_income: num(base.total_income ?? credit),
          total_expenses: num(base.total_expenses ?? debit),
          pending_contributions_amount: num(base.pending_contributions_amount ?? base.pending_contributions),
          completed_contributions_month: num(base.completed_contributions_this_month),
          ledger_tx_count: num(ledgerStatsRes.data?.volume?.count),
          ledger_volume_total: num(ledgerStatsRes.data?.volume?.total_amount),
        });
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setSnapshot(null);
          error(t('releve.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fmt = (n) => `${num(n).toLocaleString(localeFmt)} FBU`;

  const statCards = useMemo(() => {
    if (!snapshot) return [];
    return [
      {
        id: 'balance',
        title: t('releve.cardBalanceTitle'),
        subtitle: t('releve.cardBalanceSubtitle'),
        value: fmt(snapshot.total_balance),
        accent: 'border-l-[#2f6fd4]',
        iconBg: 'bg-[#edf4ff]',
        iconColor: 'text-[#113a5b]',
      },
      {
        id: 'in',
        title: t('releve.cardInTitle'),
        subtitle: t('releve.cardInSubtitle'),
        value: fmt(snapshot.total_income),
        accent: 'border-l-emerald-500',
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-800',
      },
      {
        id: 'out',
        title: t('releve.cardOutTitle'),
        subtitle: t('releve.cardOutSubtitle'),
        value: fmt(snapshot.total_expenses),
        accent: 'border-l-red-500',
        iconBg: 'bg-red-50',
        iconColor: 'text-red-800',
      },
      {
        id: 'members',
        title: t('releve.cardMembersTitle'),
        subtitle: t('releve.cardMembersSubtitle'),
        value: `${snapshot.total_members.toLocaleString(localeFmt)}`,
        suffix: snapshot.active_members
          ? t('releve.activeMembersSuffix', { count: snapshot.active_members })
          : null,
        accent: 'border-l-violet-500',
        iconBg: 'bg-violet-50',
        iconColor: 'text-violet-800',
      },
      {
        id: 'contrib',
        title: t('releve.cardContribTitle'),
        subtitle: t('releve.cardContribSubtitle'),
        value: fmt(snapshot.total_contributions),
        accent: 'border-l-amber-500',
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-900',
      },
      {
        id: 'ledger',
        title: t('releve.cardLedgerTitle'),
        subtitle: t('releve.cardLedgerSubtitle'),
        value: snapshot.ledger_tx_count
          ? t('releve.ledgerEntries', { count: snapshot.ledger_tx_count })
          : '—',
        extra: snapshot.ledger_volume_total ? fmt(snapshot.ledger_volume_total) : null,
        accent: 'border-l-slate-500',
        iconBg: 'bg-slate-100',
        iconColor: 'text-slate-800',
      },
    ];
  }, [snapshot, t, localeFmt]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] py-16">
        <div className="h-11 w-11 animate-spin rounded-full border-2 border-[#113a5b] border-t-transparent" />
        <p className="mt-4 text-sm text-slate-600">{t('releve.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] space-y-4 sm:space-y-5">
      <div className={isMembre ? 'hidden md:block' : ''}>
        <PageHeader
          title={isMaCaisse ? t('releve.pageTitleMaCaisse') : t('releve.pageTitleStatement')}
          subtitle={
            isMaCaisse
              ? t('releve.subtitleMaCaisse', { org: orgName })
              : t('releve.subtitleStatement', { org: orgName })
          }
        />
      </div>

      {isMembre && (
        <MemberMobileShell>
          <MemberMobileHero
            label={isMaCaisse ? t('releve.introKickerMaCaisse') : t('releve.introKickerOrg')}
            title={
              snapshot
                ? fmt(snapshot.total_balance)
                : t('releve.dataUnavailable')
            }
            hint={
              snapshot
                ? t('releve.membersCount', { count: snapshot.total_members })
                : undefined
            }
            tone={snapshot?.pending_contributions_amount > 0 ? 'warn' : 'info'}
            actions={
              <Link
                to="/my-fund/financial"
                className="mm-cta flex-1"
              >
                {t('releve.detailCta')}
              </Link>
            }
          />
          {snapshot?.pending_contributions_amount > 0 ? (
            <p className="mm-card border-amber-200/80 bg-gradient-to-br from-amber-50/90 to-white px-3.5 py-2.5 text-xs text-amber-950">
              <span className="font-bold">{t('releve.pendingContributions')}</span>{' '}
              {fmt(snapshot.pending_contributions_amount)}
            </p>
          ) : null}
          {snapshot ? (
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'in', label: t('releve.cardInTitle'), value: fmt(snapshot.total_income), tone: 'text-emerald-800' },
                { id: 'out', label: t('releve.cardOutTitle'), value: fmt(snapshot.total_expenses), tone: 'text-rose-800' },
                { id: 'contrib', label: t('releve.cardContribTitle'), value: fmt(snapshot.total_contributions), tone: 'text-amber-900' },
                {
                  id: 'members',
                  label: t('releve.cardMembersTitle'),
                  value: snapshot.total_members.toLocaleString(localeFmt),
                  tone: 'text-violet-900',
                },
              ].map((c) => (
                <div key={c.id} className="mm-card px-3 py-2.5">
                  <p className={`font-display text-[10px] font-bold uppercase tracking-[0.1em] ${c.tone}`}>
                    {c.label}
                  </p>
                  <p className="mt-1 font-display text-sm font-extrabold tabular-nums text-[#0f3350]">
                    {c.value}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </MemberMobileShell>
      )}

      {!snapshot ? (
        <div className={`rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 ${isMembre ? 'hidden md:block' : ''}`}>
          {t('releve.dataUnavailable')}
        </div>
      ) : (
        <>

          <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 ${isMembre ? 'hidden md:grid' : ''}`}>
            {statCards.map((c) => (
              <article
                key={c.id}
                className={`rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5 border-l-4 ${c.accent}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-xl p-2.5 ${c.iconBg} ${c.iconColor}`}>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-[#0f3350]">{c.title}</h2>
                    <p className="mt-0.5 text-xs text-slate-600">{c.subtitle}</p>
                    <p className="mt-3 text-2xl font-bold tabular-nums tracking-tight text-[#0f3350]">{c.value}</p>
                    {c.suffix ? <p className="mt-1 text-xs font-medium text-slate-600">{c.suffix}</p> : null}
                    {c.extra ? (
                      <p className="mt-1 text-sm text-slate-600">{t('releve.volumeLabel', { amount: c.extra })}</p>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {snapshot.pending_contributions_amount > 0 ? (
            <div className={`rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 ${isMembre ? 'hidden md:block' : ''}`}>
              <span className="font-semibold">{t('releve.pendingContributions')}</span>{' '}
              {fmt(snapshot.pending_contributions_amount)}
            </div>
          ) : null}

          <div className={`flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-4 sm:flex-row sm:items-center sm:justify-between ${isMembre ? 'hidden md:flex' : ''}`}>
            <p className="text-sm text-slate-700">
              <Trans i18nKey="releve.detailBlurb" components={{ strong: <strong className="font-semibold" /> }} />
            </p>
            <Link
              to="/my-fund/financial"
              className="mm-cta shrink-0"
            >
              {t('releve.detailCta')}
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default ReleveCompteOrganisation;
